// Mobile (≤880px) render of the Finance → Reports tab: the BAS card for the
// latest quarter plus one card per quarter (mockup §8.1 C). Replaces the
// `.bas-card` + `<table class="report-table">` at phone width; the desktop
// markup is untouched. The numbers must equal the desktop table cell for
// cell (see lib/financeQuarters.ts + its test).
//
// Interface is fixed (Phase C orchestration) — Finance.tsx wires this in;
// the body is built by a parallel agent. Do not change the props.
import type { QuarterReport } from '../../shared/types';
import { basSummary, quarterCardRows, quarterTitle } from '../lib/financeQuarters';

export interface FinanceReportCardsProps {
  /** Quarters already filtered to the selected FY, in the same order the desktop table renders them (API order, newest-first). */
  quarters: QuarterReport[];
  /** e.g. "FY27 Q1" — the live quarter, outlined in teal with the CURRENT tag. */
  currentQuarterKey: string;
  /** e.g. "FY27" — shown as the group count. */
  fy: string;
  /** The desktop "Export CSV →" handler; rendered as the group link. */
  onExportCsv: () => void;
}

export function FinanceReportCards({
  quarters,
  currentQuarterKey,
  fy,
  onExportCsv,
}: FinanceReportCardsProps) {
  // The page only mounts this with at least one quarter (it renders its own
  // "No dated records" note otherwise), but the BAS card reads quarters[0].
  const latest = quarters[0];
  if (!latest) return null;
  const bas = basSummary(latest);

  return (
    <div className="fr-m">
      {/* BAS card — the quarter's GST position, leading with what was collected. */}
      <div className="fr-bas">
        <div className="k">{bas.eyebrow}</div>
        <div className="big">{bas.collected}</div>
        <div className="grid">
          <div>
            <div className="k">GST paid</div>
            <div className="v g">{bas.gstPaid}</div>
          </div>
          <div>
            <div className="k">BAS position</div>
            <div className="v a">{bas.position}</div>
          </div>
          <div>
            <div className="k">Due</div>
            <div className="v">{bas.due}</div>
          </div>
        </div>
        {bas.note && <div className="fr-note">{bas.note}</div>}
      </div>

      <div className="fr-gh">
        <span className="fr-gh-t">
          Quarterly — Australian FY<span className="n">{fy}</span>
        </span>
        <button type="button" className="fr-csv" onClick={onExportCsv}>
          Export CSV →
        </button>
      </div>

      {quarters.map((q) => {
        const current = q.key === currentQuarterKey;
        return (
          <div key={q.key} className={`fr-qcard${current ? ' cur' : ''}`}>
            <div className="h">
              {quarterTitle(q.key)}
              {current && <span>CURRENT</span>}
            </div>
            <div className="g">
              {quarterCardRows(q).map((cell) => (
                <div key={cell.label}>
                  <div className="k">{cell.label}</div>
                  <div className={`v${cell.tone ? ` ${cell.tone}` : ''}`}>{cell.value}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
